import PortalItem from '@arcgis/core/portal/PortalItem';
import IdentityManager from '@arcgis/core/identity/IdentityManager';
import Layer from '@arcgis/core/layers/Layer';
import ArcGisLayerLoaderWorker from './externalLayerLoaderWorker?worker';
import type {
  ArcGisAuthOptions,
  ExternalLayerLoaderWorkerResponse,
  PortalItemWorkerResponse,
  ServiceUrlWorkerResponse,
} from './externalLayerLoaderWorker';
import { oauthPortal } from './arcGisPortal';

let worker: Worker | null = null;
let requestCount = 0;
const pendingRequests = new Map<
  string,
  {
    resolve: (response: any) => void;
    reject: (error: Error) => void;
  }
>();

function getExternalLayerLoaderWorker() {
  if (worker) return worker;

  worker = new ArcGisLayerLoaderWorker();
  worker.addEventListener(
    'message',
    (event: MessageEvent<ExternalLayerLoaderWorkerResponse>) => {
      const response = event.data;
      const deferred = pendingRequests.get(response.requestId);
      if (!deferred) {
        return;
      }

      pendingRequests.delete(response.requestId);
      deferred.resolve(response);
    }
  );
  worker.addEventListener('error', (event) => {
    const workerError = new Error(event.message || 'Layer loader worker error');

    for (const deferred of pendingRequests.values()) {
      deferred.reject(workerError);
    }
    pendingRequests.clear();
  });

  return worker;
}

export async function createPortalItem({
  portalItemId,
  options,
}: {
  portalItemId: string;
  options: ArcGisAuthOptions;
}) {
  const requestId = String(++requestCount);

  const response = await new Promise<PortalItemWorkerResponse>(
    (resolve, reject) => {
      pendingRequests.set(requestId, { resolve, reject });
      getExternalLayerLoaderWorker().postMessage({
        requestId,
        type: 'portalItem',
        portalItemId,
        options,
      });
    }
  );

  if (!response.success) {
    throw new Error(response.error);
  }

  const payload = response.payload;
  if ('apiKey' in payload) {
    const portalItem = new PortalItem({
      id: payload.id,
      apiKey: payload.apiKey,
    });
    await portalItem.load();

    if (portalItem.url) {
      IdentityManager.registerToken({
        server: portalItem.url,
        token: payload.apiKey,
      });
    }

    return portalItem;
  }

  return new PortalItem({
    id: payload.id,
    portal: oauthPortal({
      authMode: 'immediate',
      url: payload.portalUrl,
    }),
  });
}

export async function createServiceUrlLayer({
  serviceUrl,
  options,
}: {
  serviceUrl: string;
  options: ArcGisAuthOptions;
}) {
  const requestId = String(++requestCount);

  const response = await new Promise<ServiceUrlWorkerResponse>(
    (resolve, reject) => {
      pendingRequests.set(requestId, { resolve, reject });
      getExternalLayerLoaderWorker().postMessage({
        requestId,
        type: 'serviceUrl',
        serviceUrl,
        options,
      });
    }
  );

  if (!response.success) {
    throw new Error(response.error);
  }

  const payload = response.payload;
  if (payload.accessType === 'apiKey') {
    IdentityManager.registerToken({
      server: payload.url,
      token: payload.apiKey,
    });
  }

  const layer = await Layer.fromArcGISServerUrl({ url: payload.url });
  await layer.load();

  return layer;
}
